import React, { useEffect, useState, createContext, useContext, ReactNode } from 'react';
import chatSocketService, { ConnectionState } from '@/api/chat-socket-service';
import { useKeycloak } from '@react-keycloak/web';
import { checkForDeploymentNow } from '@/utils/deployment-detector';
import { useOrganization } from '@/contexts/organization-context';

// Create a context to share WebSocket state
interface WebSocketContextType {
  isConnected: boolean;
  connectionState: ConnectionState;
  connect: () => Promise<void>;
  updatePresence: (status: 'online' | 'away' | 'busy' | 'offline') => boolean;
  subscribeToRoom: (roomId: string) => boolean;
  unsubscribeFromRoom: (roomId: string) => boolean;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

// Provider component
export const WebSocketProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { keycloak, initialized } = useKeycloak();
  const { currentOrganization, currentInstitution } = useOrganization();
  const [connectionState, setConnectionState] = useState<ConnectionState>(chatSocketService.getState());
  const [connectionInitialized, setConnectionInitialized] = useState(false);
  
  // Track last presence status to avoid sending duplicate updates
  const lastPresenceStatus = React.useRef<'online' | 'away' | 'busy' | 'offline' | null>(null);
  
  // Throttled updatePresence that prevents duplicate calls
  const updatePresenceThrottled = React.useCallback((status: 'online' | 'away' | 'busy' | 'offline') => {
    if (lastPresenceStatus.current === status) {
      console.log(`WebSocketProvider: Skipping duplicate presence update: ${status}`);
      return false;
    }
    console.log(`WebSocketProvider: Updating presence from ${lastPresenceStatus.current} to ${status}`);
    lastPresenceStatus.current = status;
    return chatSocketService.updatePresence(status);
  }, []);

  // Set organization context when it changes
  useEffect(() => {
    if (!initialized) return;
    
    chatSocketService.setOrganizationContext(
      currentOrganization?.id || null,
      currentInstitution?.id || null
    );
  }, [currentOrganization?.id, currentInstitution?.id, initialized]); // Only track IDs, not objects

  // Set up event listeners and initialize connection (only once)
  useEffect(() => {
    if (!initialized || !keycloak.authenticated) return;
    if (!currentOrganization?.id || !currentInstitution?.id) return;
    if (connectionInitialized) return; // Already initialized

    console.log('WebSocketProvider: Establishing application-wide WebSocket connection');
    
    const handleStateChange = (state: ConnectionState) => {
      setConnectionState(state);
      
      // When connection is established, resubscribe to rooms
      if (state === ConnectionState.CONNECTED) {
        chatSocketService.resubscribeToRooms();
      }
      
      // If connection fails unexpectedly, check for deployment
      if (state === ConnectionState.DISCONNECTED && connectionInitialized) {
        console.log('WebSocket disconnected unexpectedly, checking for deployment');
        setTimeout(() => {
          checkForDeploymentNow();
        }, 2000);
      }
    };

    // Set up listeners
    chatSocketService.onStateChange(handleStateChange);
    
    // Initialize connection
    chatSocketService.connect().then(() => {
      updatePresenceThrottled('online');
      setConnectionInitialized(true);
    });

    // Clean up listeners on unmount - but don't disconnect
    return () => {
      chatSocketService.removeStateChangeListener(handleStateChange);
    };
  }, [initialized, keycloak.authenticated, currentOrganization?.id, currentInstitution?.id]); // Only run when these change

  // Set up authentication change handler
  useEffect(() => {
    if (!initialized) return;

    // If user logs out, disconnect WebSocket
    if (!keycloak.authenticated && connectionInitialized) {
      console.log('WebSocketProvider: User logged out, disconnecting WebSocket');
      updatePresenceThrottled('offline');
      chatSocketService.disconnect();
      setConnectionInitialized(false);
    }
  }, [keycloak.authenticated, initialized, connectionInitialized, updatePresenceThrottled]);

  // Set up page visibility change handler (less aggressive)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!keycloak.authenticated || !connectionInitialized) return;
      
      if (document.visibilityState === 'visible') {
        // Only try to reconnect if we've been disconnected for a while
        const currentState = chatSocketService.getState();
        if (currentState === ConnectionState.DISCONNECTED || currentState === ConnectionState.ERROR) {
          console.log('WebSocketProvider: Page visible and disconnected, attempting reconnect');
          // Add a small delay to avoid immediate reconnect on tab switch
          setTimeout(() => {
            if (chatSocketService.getState() === ConnectionState.DISCONNECTED) {
              chatSocketService.connect();
            }
          }, 2000);
        }
        // Update presence to online (throttled)
        updatePresenceThrottled('online');
      } else if (document.visibilityState === 'hidden') {
        // Page hidden, update presence to away (but don't disconnect)
        updatePresenceThrottled('away');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [keycloak.authenticated, connectionInitialized, updatePresenceThrottled]);

  // Before page unload, try to set offline status
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (keycloak.authenticated) {
        updatePresenceThrottled('offline');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [keycloak.authenticated, updatePresenceThrottled]);

  // Define context value
  const contextValue: WebSocketContextType = {
    isConnected: connectionState === ConnectionState.CONNECTED,
    connectionState,
    connect: chatSocketService.connect.bind(chatSocketService),
    updatePresence: updatePresenceThrottled,
    subscribeToRoom: chatSocketService.subscribeToRoom.bind(chatSocketService),
    unsubscribeFromRoom: chatSocketService.unsubscribeFromRoom.bind(chatSocketService),
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};

// Custom hook for components to access WebSocket context
export const useWebSocket = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export default WebSocketProvider; 